import '@schmancy/primitives/button/icon-button'
import { componentText, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, render as litRender, ReactiveElement, type TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import {
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	filter,
	firstValueFrom,
	fromEvent,
	map,
	merge,
	Observable,
	ReplaySubject,
	skip,
	startWith,
	Subject,
	Subscription,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import type { LazyComponent } from '@schmancy/router/lazy'
import { fromResizeObserver } from '@schmancy/directives/layout'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import { deepActiveElement, firstAutofocusTarget, focusableDescendants } from '@schmancy/utils/focus'
import { isMobile$ } from '@schmancy/utils/is-mobile'
import type { TSurfaceOpacity } from '@schmancy/types/surface'
import { anchorOriginVars, surfaceAnimation } from './overlay.animations'
import { dragToReposition$, suppressNextClick, swipeToDismiss$ } from './overlay.gestures'
import {
	anchorTrigger,
	crowdsNarrowBox,
	resolveAnchorRef,
	resolveLayout,
	sheetRenderFor,
	type LayoutInputs,
	type ResolvedAnchor,
	type SheetRender,
} from './overlay.layout'
import { pickPositioner, positionCSSAnchor, positionFloatingUI, positionPopoverAPI } from './overlay.positioning'
import { glideY } from './overlay.soft-keyboard'
import { claimInert, currentStack, inExemptLayer, inertableSibling, releaseInert } from './overlay.stack'
import type {
	Anchor,
	CloseReason,
	Content,
	OverlayBeforeCloseDetail,
	OverlayLayout,
	OverlayTier,
	ResolvedShowOptions,
} from './overlay.types'

const MOUNT_POINT_ID = 'overlay-mount'
/** visualViewport resize/scroll debounce — matches `update$`'s debounce in
 *  `@schmancy/directives/layout`'s `getUpdateStream`. */
const VIEWPORT_DEBOUNCE_MS = 16
/** iOS Safari: recalc once more after the keyboard-dismissing focusout —
 *  mirrors that same module's `focusOut$`. */
const KEYBOARD_CLOSE_RECALC_MS = 100
/** `ShowOptions.dragLabel`'s default — see that option for why it's overridable. */
const DEFAULT_DRAG_LABEL = 'Move this dialog. Use arrow keys to reposition it; hold Shift for a larger step.'

// DEV-only structural guard. Gated behind `import.meta.env.DEV`, same idiom
// as `@schmancy/directives/overflow-within`'s own `warnedElements` — nothing
// here allocates or runs in production.
//
// The failure this catches: a mounted root that paints its own opaque
// background but declares no height of its own gets STRETCHED by
// `#overlay-mount`'s `align-items: stretch` default down to the mount's row
// track whenever its real content is taller than that (the row deliberately
// carries no automatic minimum — see the comment above `#overlay-mount` in
// this file's static styles). The squeezed root's own background covers only
// its own squeezed box; content spilling past it under `overflow: visible`
// paints with none, showing `.surface`'s own glass fill through the gap —
// two visibly different fills, seamed exactly where the root's box ends.
//
// `align-items: stretch` cannot be swapped for the whole mount to fix this:
// the shipping h-full + overflowWithin() pattern (`overlay.sticky-footer.
// test.ts`'s FormShapeProbe, mirroring `event-form.view.ts`) relies on that
// same stretch to give its own undeclared-height root a DEFINITE height, so
// an inner `height: 100%` scrollport has a real number to resolve against.
// From the mount's own CSS, both consumers are indistinguishable: both are
// grid items with auto height. There is no single shared alignment value
// that serves both — confirmed by running the sticky-footer suite against
// `align-items: start`, which stretch's removal predicts would help and in
// fact strands that footer 1418px off the scrollport bottom.
//
// The actual contract a mounted root owes is `OVERLAY_MOUNTED_COMPONENT_IS_
// CONTENT_ONLY`: never a full-bleed opaque paint layer, because the overlay
// already supplies the only background a compliant consumer ever needs. A
// root that drops its own opaque fill, or that manages its own overflow
// (h-full + overflowWithin()), never reaches this gap regardless of stretch.
const mountBackgroundWarned: WeakSet<Element> | undefined = import.meta.env.DEV ? new WeakSet<Element>() : undefined

function devWarnMountBackgroundBleed(mount: HTMLElement): void {
	if (!import.meta.env.DEV) return
	const root = mount.firstElementChild
	if (!(root instanceof HTMLElement) || mountBackgroundWarned!.has(root)) return
	const style = getComputedStyle(root)
	// A root managing its own overflow (h-full + overflowWithin(), or any
	// overflow-y other than visible) cannot reach this gap: whatever spills
	// past its box is clipped or scrolled within it, not painted past it.
	if (style.overflowY !== 'visible') return
	if (root.scrollHeight <= root.clientHeight + 1) return
	const paintsBackground =
		(style.backgroundColor !== 'rgba(0, 0, 0, 0)' && style.backgroundColor !== 'transparent') ||
		style.backgroundImage !== 'none'
	if (!paintsBackground) return
	mountBackgroundWarned!.add(root)
	console.warn(
		`[schmancy-overlay] <${root.tagName.toLowerCase()}> paints its own background but its own box ` +
			`(${Math.round(root.clientHeight)}px) is shorter than its rendered content ` +
			`(${Math.round(root.scrollHeight)}px) with overflow: visible — the excess paints with no background ` +
			"of its own, showing the overlay's own glass fill through the gap. Per " +
			'OVERLAY_MOUNTED_COMPONENT_IS_CONTENT_ONLY, a mounted root never paints a full-bleed opaque ' +
			"background — drop it and let the overlay's own glass fill show, or give this root its own " +
			'`h-full` + `overflowWithin()` scrollport so its overflow is managed internally instead of spilling.',
		root,
	)
}

/**
 * Custom `<div>` shell (not a native `<dialog>`) — one backdrop
 * mechanism, one focus-trap path, one animation orchestrator across
 * every layout/tier combination. The shell is always rendered; the
 * backdrop only renders when modal.
 */
@customElement('schmancy-overlay')
export class SchmancyOverlay extends SchmancyElement {
	static styles = [
		css`
			:host {
				position: fixed;
				inset: 0;
				z-index: 10000;
				display: none;
				pointer-events: none;
			}
			:host([maximized]) .surface {
				position: fixed !important;
				inset: 0 !important;
				margin: 0 !important;
				width: 100dvw !important;
				height: 100dvh !important;
				max-width: none !important;
				max-height: none !important;
				transform: none !important;
				border-radius: 0;
			}
			:host([maximized][bounded]) .surface {
				position: absolute !important;
				width: 100% !important;
				height: 100% !important;
			}
			:host([maximized]) .drag-handle {
				visibility: hidden;
			}
			:host([maximized]) #overlay-mount > * {
				width: 100% !important;
				height: 100% !important;
				max-width: none !important;
				max-height: none !important;
				min-width: 0;
				min-height: 0;
			}
			:host([active]) {
				display: block;
			}
			/* COVERED — a modal overlay is stacked above this one, so claimInert()
			 * has set inert here: everything inside is already unfocusable,
			 * untappable and out of the accessibility tree. The one thing inert does
			 * not do is stop it being PAINTED, and a dead control is worse than an
			 * absent one — it invites a tap that silently does nothing. A ground used
			 * to hide that; a full-bleed viewer with no ground leaves a live close
			 * and a dead one inches apart.
			 *
			 * A custom property because nothing else crosses a shadow boundary:
			 * content renders its controls in its own shadow root, which no selector
			 * of ours reaches. Unset reads 0, so nothing that has not opted in moves.
			 *
			 * Two deliberate limits. This :host matches only a schmancy-overlay, so
			 * the plain page behind a modal — which has a backdrop to say it is
			 * suspended — keeps its controls. And only BUTTONS honour it: a button
			 * inert carries nothing, where a switch or field still shows its value,
			 * and the page behind an opened photo is meant to stay readable. */
			:host([inert]) {
				--schmancy-overlay-covered: 1;
			}
			/* Grid blockifies mounted content's USED display (inline→block,
			 * inline-flex→flex), so show() content doesn't need to declare
			 * :host{display:block} itself — a SchmancyElement host has no default
			 * :host display, and unblockified content silently drops its own
			 * padding/sizing.
			 *
			 * The single row is pinned to minmax(0,1fr) so the mounted child
			 * inherits the mount's DEFINITE height: content sizes against the
			 * overlay, a bare <img>/<embed> taller than the sheet clips to it
			 * instead of pushing past 90svh, and — the load-bearing one — a
			 * component whose root is h-full + overflowWithin() resolves that
			 * height:100% against a real number, so its own sticky bottom-0
			 * footer has a bounded scrollport to pin to. With the row at auto
			 * the child grows to content height instead: its own scroller never
			 * scrolls, the mount scrolls around it, and the footer strands at the
			 * end of content (measured at 390x844: 1352px below the visible
			 * scrollport edge) — the FOOTER_FLOATS_ON_GLASS promise silently off.
			 *
			 * Declared here, not as a grid-rows-[minmax(0,1fr)] class on the
			 * mount: that arbitrary value nests parentheses, which Tailwind 4's
			 * extractor does not reliably emit, and an unemitted class turns the
			 * whole contract above off with nothing thrown. Static CSS is the
			 * surface where presence is guaranteed. */
			#overlay-mount {
				display: grid;
				grid-template-rows: minmax(0, 1fr);
				/* The column is pinned for the same reason the row above it is, one
				 * axis over. An auto track sizes to its item's max-content, so the
				 * grid AREA a percentage resolves against becomes the mounted panel's
				 * own measured width — which is why a panel's max-w-full clamps
				 * nothing inside an anchored surface (w-max, itself already capped at
				 * 100dvw - 2rem): the percentage resolves against the panel, not
				 * against the box the viewport left it. Pinned to minmax(0,1fr) the
				 * area is the mount's own definite width, so the viewport bound the
				 * surface already carries reaches the content — and a consumer stops
				 * needing a max-w-[calc(100vw-2rem)] of its own to stay reachable at
				 * 390px.
				 *
				 * Nothing moves for content that fits: an auto track in a
				 * definite-width grid container is stretched to fill it anyway
				 * (CSS Grid 12.8), so the two agree everywhere except the overflow
				 * this exists to catch.
				 *
				 * What the pin gives up, deliberately: an auto track carries an
				 * automatic minimum size, so it grows past the container rather than
				 * squeeze its item below min-content. minmax(0,1fr) drops that floor,
				 * so a panel whose min-content is wider than the mount now overflows
				 * instead of widening the track. That is the trade this pin exists to
				 * make — the far side of an overflowing panel stays reachable because
				 * the mount is the scrollport (the third clamp test pins exactly
				 * that), where the widened track put it off screen behind the
				 * surface's own overflow-hidden with nothing to scroll. */
				grid-template-columns: minmax(0, 1fr);
			}
			/* color-mix percentages aren't reachable as Tailwind utilities. */
			.backdrop {
				background: color-mix(in srgb, var(--schmancy-sys-color-scrim) 28%, transparent);
				-webkit-backdrop-filter: blur(6px);
				backdrop-filter: blur(6px);
			}
			/* WebKit drops a backdrop-filter composed through Tailwind's chained
			 * --tw-backdrop-* vars (tailwindlabs/tailwindcss#16019) — declared here
			 * instead of via backdrop-blur-[...]/backdrop-saturate-[...] utilities.
			 * A lone var() per filter function (not a multi-var chain) is the
			 * already-shipped, WebKit-safe recipe — mirrors <schmancy-surface>'s
			 * [elevated] rule, which reads the same glass tokens the same way.
			 *
			 * Every glass var() carries its theme default as a fallback. The tokens
			 * are emitted on the theme element's own :host, so an overlay that
			 * mounts outside that subtree (show() falls back to document.body when no
			 * theme element is found) resolves NOTHING — and an unresolved var() is
			 * invalid-at-computed-value-time, which drops the WHOLE declaration, not
			 * just the token. Without fallbacks that failure mode is an invisible
			 * surface: no blur, and the fill below collapses to transparent too. */
			.surface {
				-webkit-backdrop-filter: blur(var(--schmancy-sys-glass-blur, 24px))
					saturate(var(--schmancy-sys-glass-saturate, 180%));
				backdrop-filter: blur(var(--schmancy-sys-glass-blur, 24px)) saturate(var(--schmancy-sys-glass-saturate, 180%));
			}
			/* The mount owns scrolling. overflow:hidden would also let focus pan
			 * this outer frame, moving the mounted header behind its own clip. */
			:host(:not([layout='viewer'])) .surface {
				overflow: clip;
			}
			/* Surface fill — the same three-tier scale <schmancy-surface opacity>
			 * exposes, so the overlay and the surface primitive stay one glass
			 * vocabulary. An anchored tier (css-anchor/popover-fui/fui-only) has
			 * no backdrop behind it, so this fill is the only thing standing
			 * between mounted content and whatever the overlay opened over.
			 * Same fallback reasoning as the backdrop-filter rule above: an
			 * unresolved token would invalidate the whole background declaration
			 * and leave the surface fully transparent. */
			:host([opacity='glass']) .surface {
				background: color-mix(in srgb, var(--schmancy-sys-color-surface-container, Canvas) 28%, transparent);
			}
			:host([opacity='translucent']) .surface {
				background: color-mix(
					in srgb,
					var(--schmancy-sys-color-surface-container, Canvas) var(--schmancy-sys-glass-fill, 50%),
					transparent
				);
			}
			:host([opacity='solid']) .surface {
				background: color-mix(in srgb, var(--schmancy-sys-color-surface-container, Canvas) 94%, transparent);
			}
			/* Both fallbacks below MUST outrank the three :host([opacity=…]) .surface
			 * rules above — those are (0,2,0), so a bare .surface (0,1,0) silently
			 * loses and the fallback never lands. :host([opacity]) matches whatever
			 * tier is reflected, ties at (0,2,0), and wins on source order.
			 *
			 * Without backdrop-filter the translucent fill sits alone over live page
			 * content and is unreadable, so opaque all the way up. */
			@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
				:host([opacity]) .surface,
				:host .surface {
					background: var(--schmancy-sys-color-surface-container, Canvas);
				}
			}
			/* Reduced-transparency (Apple HIG accessibility). Strips the blur, so the
			 * fill has to go near-opaque in the same breath — stripping blur while
			 * leaving a 50% fill is the unreadable state, not the accessible one.
			 * NOTE: WebKit does not implement prefers-reduced-transparency
			 * (bug 175497), so this never matches on macOS/iOS Safari — the very
			 * platforms whose Reduce Transparency setting it exists to honour.
			 * Reaching those needs an app-level toggle, not this query. */
			@media (prefers-reduced-transparency: reduce) {
				:host([opacity]) .surface,
				:host .surface {
					background: color-mix(in srgb, var(--schmancy-sys-color-surface-container, Canvas) 95%, transparent);
					backdrop-filter: none;
					-webkit-backdrop-filter: none;
				}
				.backdrop {
					backdrop-filter: none;
					-webkit-backdrop-filter: none;
				}
			}
			/* Bounded: the boundary is the offset parent, so inset:0 is ITS box and
			 * its overflow:hidden clips. Every layer drops out of position:fixed —
			 * a fixed descendant of an absolute host still resolves against the
			 * viewport unless an ancestor carries contain, which the embedder cannot
			 * be relied on to set.
			 *
			 * render()'s svh/dvw caps measure the real window, so they are restated
			 * here against the frame: the whole width the frame gives, and 90% of its
			 * height — the same fraction max-h-[90svh] takes of a real viewport, so a
			 * sheet drawn in a phone frame leaves the same strip of ground above it
			 * that the same sheet leaves on the phone. Unlayered, so these outrank the
			 * Tailwind caps, which live in @layer utilities. */
			:host([bounded]) {
				position: absolute;
			}
			:host([bounded]) .shell,
			:host([bounded]) .backdrop {
				position: absolute;
			}
			:host([bounded]) .surface[data-tier='modal'] {
				position: absolute;
				max-width: 100%;
				max-height: 90%;
			}
			/* The viewer has no surface of its own: the content IS the thing being
			 * looked at, and every pixel around it is ground. No fill, no blur, no
			 * hairline — only the content, over the near-opaque scrim below.
			 *
			 * THIS BLOCK'S POSITION IS LOAD-BEARING, for the same reason the
			 * @supports/@media fallbacks above say theirs is. It is (0,3,0), which
			 * TIES with :host([opacity=…]) .surface, with the no-backdrop-filter
			 * fallback's :host([opacity]) .surface, and with the reduced-transparency
			 * one — @supports and @media add no specificity, so source order is the
			 * whole tiebreak. Authored up beside the other :host([opacity=…]) rules,
			 * where it reads like it belongs, it would LOSE to both fallbacks: a
			 * reduced-transparency user would get a 95%-opaque theme-coloured pane
			 * across the entire viewport instead of the photo, and so would every
			 * browser without backdrop-filter. It goes last, after every rule it must
			 * outrank.
			 *
			 * And never as a Tailwind class on the surface's class string: the
			 * compiled Tailwind sheet is @layered, and a layered declaration loses to
			 * an unlayered one at any specificity (the same mechanism the comment on
			 * :host([bounded]) above relies on) — a bg-transparent/backdrop-blur-none/
			 * border-0 there would be beaten by these very rules and the viewer would
			 * open as translucent glass with a 24px blur in every browser. border: 0
			 * is here rather than as a border-0 class for that reason too: the
			 * hairline baseClasses gives every surface is a layered utility this
			 * unlayered declaration outranks, where a second utility would merely tie
			 * with it inside the same layer. The corner is here for the same reason,
			 * and is the deliberate 0 the radius ladder admits: no rung of that
			 * ladder is right for a surface that has no corner to round. */
			:host([layout='viewer']) .surface {
				background: transparent;
				backdrop-filter: none;
				-webkit-backdrop-filter: none;
				border: 0;
				border-radius: 0;
			}
			/* NOTHING IS DRAWN AROUND THE PICTURE — and there is no longer any layer
			 * here to draw it on. A viewer is not modal, so the when(this.modal, …)
			 * guard in render() emits no backdrop element at all; the two rules that
			 * used to blank one are gone with it, and so is the bounded rule beside
			 * them, which could only match while a viewer could be bounded — show()
			 * now refuses a viewer a boundary outright.
			 *
			 * The requirement they served stands and is worth keeping written down.
			 * Three treatments were built and all three were rejected by the product
			 * owner, each for the same reason: a scrim, then a warm halo, then a
			 * backdrop blur. Each was something drawn AROUND the picture. It is not a
			 * request for a subtler surface; it is a request for no surface. The page
			 * behind stays exactly as it is, and the picture is simply on top of it.
			 *
			 * What that costs is real and was accepted knowingly: whatever sits under
			 * an opened photo remains fully legible. Do not reintroduce a fill, a
			 * filter or a gradient to fix that. It has been tried three times. The
			 * picture's own ground, which schmancy-lightbox draws inside its own box,
			 * is a different thing and not this. */
			/* UA popover defaults push the surface off-screen. */
			.surface:popover-open {
				margin: 0;
				border: 0;
			}
			/* Promotes the whole shell into the top layer so every overlay —
			 * anchored or modal — shares one stacking domain ordered strictly
			 * by show() time. Neutralize the UA popover box so the shell stays
			 * full-viewport. */
			.shell:popover-open {
				inset: 0;
				margin: 0;
				border: 0;
				width: 100%;
				height: 100%;
				max-width: none;
				max-height: none;
				background: transparent;
				overflow: visible;
			}
		`,
	]

	@property({ type: String, reflect: true }) layout: OverlayLayout = 'sheet'
	@property({ type: Boolean, reflect: true }) dismissable = true
	/**
	 * The page is inert behind this surface: a backdrop covers it, siblings are
	 * inerted, `aria-modal` is true. That is a fact about how much room the
	 * surface takes, so it follows the layout — a sheet covers the page, an
	 * anchored panel sits beside a trigger and leaves it working.
	 *
	 * A sibling of the mount carrying `data-overlay-exempt` (presence, any
	 * value) is left live — the way a native modal `<dialog>` leaves later
	 * top-layer elements live. For a layer in `<body>` beside the app that must
	 * keep taking taps under every modal, including one it opened itself.
	 */
	@property({ type: Boolean, reflect: true }) modal = true
	/**
	 * The content is the answer to what the user just did, so the surface is a
	 * `dialog` however it is laid out, and it survives a pointerdown that lands
	 * elsewhere — see `ShowOptions.sticky`.
	 */
	@property({ type: Boolean, reflect: true }) sticky = false
	/**
	 * Resolved `ShowOptions.drag` — already the effective value (requested
	 * AND supported by the current layout/tier), never the raw request. See
	 * that option's doc for the tier scoping and the tracking/reset behaviour
	 * this drives; `wireDragToReposition` and the `.drag-handle` in `render()`
	 * are the two places that read it.
	 */
	@property({ type: Boolean, reflect: true }) drag = false
	@state() private fileViewer = false
	@property({ type: Boolean, reflect: true }) maximized = false
	/** `ShowOptions.dragLabel`, defaulted in `open()`. Read only by the
	 *  `.drag-handle`'s `aria-label` in `render()`. */
	@property({ type: String }) dragLabel = DEFAULT_DRAG_LABEL
	@property({ type: String, reflect: true }) tier: OverlayTier = 'modal'
	@property({ type: String, reflect: true }) opacity: TSurfaceOpacity = 'translucent'
	/**
	 * Present when this overlay is scoped to a boundary box — `ShowOptions.boundary`,
	 * or the `[data-fill-boundary]` frame the opening gesture came from — rather
	 * than to the whole document: positioned and sized against that box, and never
	 * promoted to the top layer, which no ancestor can clip. Set by the service;
	 * false on every ordinary surface.
	 */
	@property({ type: Boolean, reflect: true }) bounded = false

	@state() private active = false
	/**
	 * Which of `sheet`'s two renders this overlay shows. Stays `'media'` — the
	 * window's own `md:` prefixes — for every unbounded overlay; a bounded one
	 * measures its boundary box and resolves the variant itself, since a media
	 * query can only ever see the window.
	 */
	@state() private sheetRender: SheetRender = 'media'
	// Named surfaceLabel, not ariaLabel: every Element already has a native
	// ariaLabel accessor reflecting onto the HOST's own attribute; this drives
	// the internal <section> that actually carries the dialog/region role.
	@state() private surfaceLabel?: string
	/**
	 * A sheet whose content a gesture anchored and which crowded the narrow box
	 * it opened in (`crowdsNarrowBox`). That content was written as a panel beside
	 * its trigger, whose dismiss path was the tap elsewhere, so it rarely renders a
	 * close of its own: the sheet draws its grab handle and a labelled close.
	 */
	@state() private sheetChrome = false
	@state() private contentClose = false

	@query('.backdrop') private backdrop?: HTMLDivElement
	@query('.surface') private surface!: HTMLElement
	@query('.shell') private shell?: HTMLElement
	@query('.drag-handle') private dragHandleEl?: HTMLElement

	readonly #closed$ = new Subject<{ reason: CloseReason; result?: unknown }>()

	private mounted = false
	private closing = false
	private resolvedAnchor?: ResolvedAnchor
	/** Everything `resolveLayout` reads, authored once in `open()` and never
	 *  mutated after — so the layout an overlay opens with is the layout it
	 *  keeps for its whole life. */
	private layoutInputs: LayoutInputs = {}
	/** The box every fitting decision measures against, when there is one. The
	 *  sheet reads it through the host's own rect (`inset:0` inside it); an
	 *  anchored surface reads it as Floating UI's clipping boundary. */
	private boundaryEl?: HTMLElement
	private anchorOriginAnchor?: Anchor
	/** `ResolvedShowOptions.trigger` — the element the opening gesture landed on:
	 *  the viewer's FLIP entrance grows from its box, and focus returns to it on
	 *  close when nothing held focus at open. */
	private triggerEl?: HTMLElement
	private triggerRect?: DOMRect
	private positioned$ = new ReplaySubject<{ width: number; height: number }>(1)
	private positionerTeardown?: () => void
	private factoryEffectDispose?: () => void
	private lastFocusedElement: HTMLElement | null = null
	private inertedSiblings: HTMLElement[] = []
	private tabTrapHandler?: (e: KeyboardEvent) => void
	/** The Floating-UI `autoUpdate` subscription for the `popover-fui`/
	 *  `fui-only` tiers — `wireDragToReposition` unsubscribes it on the
	 *  surface's first drag/nudge, per `ShowOptions.drag`'s tracking rule.
	 *  Unset for every other tier (nothing to tear down). */
	private trackingSub?: Subscription

	get closed$(): import('rxjs').Observable<{ reason: CloseReason; result?: unknown }> {
		return this.#closed$.asObservable()
	}

	async open(content: Content, options: ResolvedShowOptions, joinStack?: () => void): Promise<void> {
		if (this.mounted) throw new Error('schmancy-overlay: open() called twice on the same element')
		this.mounted = true
		this.maximized = false

		this.dismissable = options.dismissable !== false
		this.opacity = options.opacity ?? 'translucent'
		// Written on the host before first paint, not on `.surface` directly:
		// every reader inside this shadow root — `.surface`'s own fill,
		// `data-sheet-chrome` (which paints none of its own), and the mounted
		// content via ordinary custom-property inheritance — resolves the same
		// value from the same ancestor. See `ShowOptions.surfaceVars`.
		if (options.surfaceVars) {
			for (const [name, value] of Object.entries(options.surfaceVars)) this.style.setProperty(name, value)
		}
		this.surfaceLabel = options.ariaLabel
		this.anchorOriginAnchor = options.anchor
		this.triggerEl = options.trigger
		if (options.layout === 'viewer' && this.triggerEl?.isConnected) {
			const rect = this.triggerEl.getBoundingClientRect()
			if (rect.width > 0 && rect.height > 0) {
				this.triggerRect = rect
				this.anchorOriginAnchor = { x: rect.left, y: rect.top }
			}
		}
		this.sticky = options.sticky ?? false
		this.dragLabel = options.dragLabel ?? DEFAULT_DRAG_LABEL
		this.resolvedAnchor = resolveAnchorRef(this.anchorOriginAnchor)
		this.boundaryEl = options.boundary

		// A gesture-anchored open almost always resolves to the anchored,
		// non-modal tier once its content is measured below — but `modal`
		// and `layout` default to the full sheet until then. Committing that
		// default on first paint, only to discard it for the anchored panel
		// the instant content mounts, painted a full-screen backdrop that
		// flashed and vanished — worse the longer a lazy-loaded overlay took
		// to import. Starting from the likely end state here skips that.
		if (this.resolvedAnchor !== undefined && options.layout !== 'viewer') {
			this.layout = 'anchored'
			this.modal = false
		}

		// Ensure the shell is rendered so the mount point exists.
		this.active = true
		this.setAttribute('active', '')
		await this.updateComplete

		const mount = this.renderRoot.querySelector(`#${MOUNT_POINT_ID}`) as HTMLElement | null
		if (!mount) throw new Error('schmancy-overlay: mount point missing')
		if (isTemplateFactory(content)) {
			// The overlay IS a SignalWatcher (SchmancyElement), so updateEffect
			// re-runs litRender whenever a signal the factory reads changes.
			// Content stays in the light-DOM mount point — no shadow boundary,
			// every event / focus / ResizeObserver contract preserved.
			await new Promise<void>((resolve, reject) => {
				this.factoryEffectDispose = this.updateEffect(() => {
					try {
						litRender(content(), mount)
						resolve()
					} catch (error) {
						reject(error)
					}
				}, { manualDispose: true })
			})
		} else {
			// An eager template read its values at the call site and gets no effect
			// above, so the surface holds what was true before it opened while the
			// state behind it moves on — with nothing thrown and nothing to see.
			if (import.meta.env.DEV && isTemplateResult(content)) {
				console.warn(
					'[schmancy-overlay] show() received an already-evaluated template. Its values were ' +
						'read once at the call site and this surface will never re-render — wrap it in a ' +
						'factory so the values are read at mount time and stay reactive: show(() => html`…`).',
				)
			}
			await mountContent(content, mount, options.props)
		}

		// Whether the content crowds the box is measured only where it can: a
		// gesture anchored the open and the box is narrow enough for the sheet's
		// bottom render. The content is laid out once as the anchored panel it
		// would be, and read once every element it renders has committed its own
		// render. That wait is microtasks only, so nothing paints in between and
		// no event the content raises meanwhile — a `close` it dispatches on its
		// first render — falls into a gap before the close triggers are wired.
		// A wide box skips the measurement entirely, so its anchored panel opens
		// exactly as before.
		const box = this.bounded
			? this.getBoundingClientRect()
			: { width: window.innerWidth, height: window.innerHeight }
		// Content that is the whole room says so on its own root. In a frame, the frame
		// is the room: it takes the frame's box and stays laid out inside it.
		const fullscreen = !!mount.querySelector(':scope > [data-overlay-fullscreen]')
		let crowded = false
		if (options.layout !== 'viewer' && !fullscreen && this.resolvedAnchor !== undefined && sheetRenderFor(box.width) === 'bottom') {
			this.layout = 'anchored'
			this.modal = false
			await this.updateComplete
			// Each pass awaits the renders standing now, then descends into the
			// shadow roots those renders produced.
			const rendered = async (root: Element | ShadowRoot): Promise<void> => {
				const elements = [...root.querySelectorAll('*')]
				await Promise.all(elements.map(el => (el instanceof ReactiveElement ? el.updateComplete : undefined)))
				await Promise.all(elements.map(el => (el.shadowRoot ? rendered(el.shadowRoot) : undefined)))
			}
			await rendered(mount)
			crowded = crowdsNarrowBox(this.surface.getBoundingClientRect(), box)
		}

		// The resolver is the only thing that picks a layout, and this is the one
		// place its inputs are authored, so nothing downstream assembles a second
		// set free to drift from it. A boundary names the viewport the surface
		// measures against and no layout; the gesture and the measured content
		// decide where the surface belongs.
		this.layoutInputs = {
			anchor: this.resolvedAnchor,
			bounded: options.boundary !== undefined,
			viewer: options.layout === 'viewer' || fullscreen,
			crowded,
		}
		this.layout = resolveLayout(this.layoutInputs)
		this.maximized = fullscreen
		this.fileViewer = !!mount.querySelector('[data-overlay-viewer]')
		this.sheetChrome = crowded && this.layout === 'sheet' && this.dismissable && !this.fileViewer

		// Only the sheet is modal. A panel beside its trigger leaves the page live
		// behind it, and so does a viewer — which is deliberate, and is what keeps
		// the viewer on the ordinary positioning path: the 'modal' tier skips
		// `pickPositioner` entirely and leaves the surface to place itself from its
		// own classes, which is how a photo ends up pinned and centred by hand
		// instead of opening where it was touched. A photo opens the way every other
		// `show()` opens, from the anchor the gesture gave it, so it takes the same
		// route.
		//
		// It renders no `.backdrop` as a result, and loses nothing by it: that layer
		// painted nothing for a viewer anyway. What a viewer does still take from
		// the modal side is focus containment — see `wireFocusTrap`, which gates on
		// the LAYOUT as well as on `modal`, because what is behind a picture should
		// not be reachable by Tab however the surface is placed.
		this.modal = this.layout === 'sheet'

		// `modal: true` on an anchored layout still resolves to the 'modal' tier.
		this.tier = this.modal || (fullscreen && this.bounded)
			? 'modal'
			: this.resolvedAnchor
				? pickPositioner(this.resolvedAnchor, this.renderRoot, this.boundaryEl)
				: 'modal'

		// An anchored surface is placed from a viewport rect and lives in the top
		// layer, which no ancestor's overflow can clip — so `bounded`'s own
		// containment (absolute inside the frame, out of the top layer) does not
		// apply to it, and the attribute is cleared to keep one attribute as the
		// single answer to whether this overlay is laid out inside its frame's box.
		// The frame still rules that surface's geometry: it rides into Floating UI
		// as the clipping box every flip, shift, and size clamp is measured against
		// (see `boundaryEl` below), so the anchored panel fits the frame rather than
		// the window it cannot see.
		if (this.bounded && this.tier !== 'modal') this.bounded = false

		// The host is `position:absolute; inset:0` when bounded, so it IS the
		// containing block every layer below it resolves against — its own rect is
		// the box the sheet lays out in, whatever the boundary's ancestor chain
		// does. Read once here, while `bounded` is final and before the surface
		// first paints its variant; `wireBoundedSheetRender` keeps it current.
		this.sheetRender = this.bounded ? sheetRenderFor(this.getBoundingClientRect().width) : 'media'

		// `drag` is resolved here, not just requested — a translate offset
		// composes cleanly on top of every anchored tier and the sheet's
		// centered/desktop render, but fights the bottom-sheet render's own
		// vertical drag for swipe-to-dismiss (CloseReason: 'swipe'), so that
		// render never gets it however it was asked for.
		//
		// The viewer refuses it outright, and by name: it has no `sheetRender` of
		// its own, so the sheet branch below would read its default `media` and
		// hand a desktop viewer a drag handle — a grab bar on a transparent
		// full-viewport surface, which translates the whole ground off-centre and
		// exposes the page at one edge. There is nothing to reposition; the
		// surface IS the viewport.
		const requestedDrag = options.drag ?? (this.fileViewer || this.sticky)
		const dragSupported =
			this.layout === 'anchored'
				? true
				: this.layout === 'viewer'
					? false
					: this.sheetRender === 'centered' || (this.sheetRender === 'media' && !isMobile$.value)
		this.drag = requestedDrag && dragSupported
		if (import.meta.env.DEV && requestedDrag && !dragSupported) {
			console.warn(
				this.layout === 'viewer'
					? '[schmancy-overlay] drag:true has no effect on a viewer — its surface is the ' +
							'whole viewport, so there is nowhere to reposition it to. `drag` is supported ' +
							"on anchored surfaces and the sheet layout's centered/desktop render."
					: '[schmancy-overlay] drag:true has no effect on a mobile bottom sheet — ' +
							'that render already owns vertical drag for swipe-to-dismiss. `drag` is ' +
							"supported on anchored surfaces and the sheet layout's centered/desktop render.",
			)
		}

		await this.updateComplete

		// The anchor-origin bloom is a one-shot entrance treatment, so the FUI
		// tiers set it on the FIRST positioning emission only — later autoUpdate
		// ticks (scroll/resize) must not rewrite the origin mid-life.
		let originSet = false
		if (this.tier === 'css-anchor' && this.resolvedAnchor?.el && this.shadowRoot) {
			this.positionerTeardown = positionCSSAnchor(this.surface, this.resolvedAnchor, this.shadowRoot, {
				id: `ov-${Math.random().toString(36).slice(2, 10)}`,
				placement: options.preferredPlacement ?? 'bottom-start',
			})
			// Pair with the Popover API for the native top layer. `popover="manual"`
			// (overlay.positioning.ts), so there is no native light-dismiss and no
			// native Esc — schmancy owns both, in wireCloseTriggers.
			const popoverCleanup = positionPopoverAPI(this.surface)
			const cssAnchorTeardown = this.positionerTeardown
			this.positionerTeardown = () => {
				popoverCleanup()
				cssAnchorTeardown?.()
			}
		} else if (this.tier === 'popover-fui' && this.resolvedAnchor) {
			const popoverCleanup = positionPopoverAPI(this.surface)
			const floatSub = positionFloatingUI(this.surface, this.resolvedAnchor, {
				placement: this.triggerRect ? 'bottom-start' : (options.preferredPlacement ?? 'bottom-start'),
				offsetPx: this.triggerRect ? 0 : (options.offset ?? 8),
				coverSource: this.triggerRect !== undefined,
				track: options.track !== false,
				animationFrame: options.trackAnimationFrame === true,
				grow: options.grow ?? true,
				boundary: this.boundaryEl,
			})
				.pipe(takeUntil(this.disconnecting))
				.subscribe({
					next: () => {
						if (!originSet) {
							originSet = true
							this.setAnchorOriginVars()
						}
						this.positioned$.next({ width: this.surface.offsetWidth, height: this.surface.offsetHeight })
					},
					error: error => this.positioned$.error(error),
				})
			this.trackingSub = floatSub
			this.positionerTeardown = () => {
				popoverCleanup()
				floatSub.unsubscribe()
			}
		} else if (this.tier === 'fui-only' && this.resolvedAnchor) {
			const floatSub = positionFloatingUI(this.surface, this.resolvedAnchor, {
				placement: this.triggerRect ? 'bottom-start' : (options.preferredPlacement ?? 'bottom-start'),
				offsetPx: this.triggerRect ? 0 : (options.offset ?? 8),
				coverSource: this.triggerRect !== undefined,
				track: options.track !== false,
				animationFrame: options.trackAnimationFrame === true,
				grow: options.grow === true,
				boundary: this.boundaryEl,
			})
				.pipe(takeUntil(this.disconnecting))
				.subscribe({
					next: () => {
						if (!originSet) {
							originSet = true
							this.setAnchorOriginVars()
						}
						this.positioned$.next({ width: this.surface.offsetWidth, height: this.surface.offsetHeight })
					},
					error: error => this.positioned$.error(error),
				})
			this.trackingSub = floatSub
			this.positionerTeardown = () => floatSub.unsubscribe()
		} else if (this.tier === 'modal' && this.shell && !this.bounded) {
			// Promotes the shell into the same top-layer domain anchored tiers
			// use, so a sheet opened from inside an earlier anchored overlay
			// correctly stacks above it. Degrade silently to the legacy
			// z-index host on browsers without the Popover API.
			//
			// Skipped when bounded: the top layer renders outside the whole ancestor
			// chain, so a promoted shell ignores the boundary's clip however its
			// boxes are positioned. A bounded overlay stacks on the host z-index
			// against the few elements inside one frame instead.
			try {
				this.positionerTeardown = positionPopoverAPI(this.shell)
			} catch {
				// no Popover API support — keep the host z-index:10000 path.
			}
		}

		// FUI tiers (popover-fui, fui-only) set the anchor-origin vars post-
		// first-positioning-pass via the positionFloatingUI subscribe callback —
		// the rect is final only after Floating UI has run. css-anchor and
		// modal place synchronously, so the rect is already final here.
		if (this.tier !== 'popover-fui' && this.tier !== 'fui-only') {
			this.setAnchorOriginVars()
		}

		if (import.meta.env.DEV) {
			merge(timer(0), fromResizeObserver(mount))
				.pipe(debounceTime(100), takeUntil(this.disconnecting))
				.subscribe(() => devWarnMountBackgroundBleed(mount))
		}

		// WebKit answers a new shadow root's media queries from the window as it was before a resize or a rotation, so a
		// phone's sheet can open as the desktop card. Adopting the same sheets again makes it read them now.
		if (this.shadowRoot) this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets]
		// Before focus can land in it: only the top of the stack answers Escape, and a person may press it at once.
		joinStack?.()
		this.wireFocusTrap()
		// A sticky, non-modal surface draws no backdrop and no longer closes on
		// a stray click elsewhere — Escape is then the ONLY dismiss path left
		// for content with no focusable control of its own, and Escape is
		// undiscoverable to a pointer/touch user.
		if (import.meta.env.DEV && this.sticky && this.tier !== 'modal' && this.surface) {
			// Raised off a macrotask rather than read here: the mounted content is a
			// custom element whose FIRST render is a later microtask than this one,
			// and whose own children render in the microtasks after that — so read
			// inline the surface is an empty tag, and this warning fired on every
			// sticky overlay whose content renders its own close control, which is
			// every surface the option was written for. Measured on
			// <schmancy-date-field-dialog>: 0 focusable here, 5 one task later.
			timer(0)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => {
					// The drag handle is focusable (arrow-key nudging needs that), but it
					// repositions the surface — it is not the close control this warning
					// is checking for, so it doesn't count toward "has focusable content".
					const closableContent = focusableDescendants(this.surface).filter(el => !el.classList.contains('drag-handle'))
					if (closableContent.length === 0) {
						console.warn(
							'[schmancy-overlay] sticky:true with no focusable content in the mounted surface — ' +
								'the only way to close this overlay is Escape, which most users will never find. ' +
								'Render a close control in the mounted content, or drop `sticky` and let outside-click dismiss it.',
						)
					}
				})
		}
		this.wireTabTrap()
		this.wireCloseTriggers(options.signal)
		this.wireContentClose(mount)
		this.wireBoundedSheetRender()
		this.wireKeyboardAvoidance()
		this.wireDragToReposition()

		// will-change:transform is scoped to the entrance only — permanent
		// will-change allocates a GPU layer for the element's lifetime and
		// thrashes memory.
		this.surface.style.willChange = 'transform'
		try {
			await this.playAnimations('in')
		} finally {
			// A concurrent close() can already have set active=false and blanked
			// @query('.surface') by the time this finally runs (close() only
			// guards on this.closing/this.mounted, both already true this early
			// in open()) — guard the write, not the read four lines above.
			if (this.surface) this.surface.style.willChange = ''
		}
	}

	async close(reason: CloseReason, result?: unknown): Promise<void> {
		if (this.closing || !this.mounted) return
		this.closing = true
		try {
			await this.playAnimations('out')
		} catch {
			// animation cancelled mid-flight — not an error.
		}
		this.releaseFocusTrap()
		this.releaseTabTrap()
		if (this.factoryEffectDispose) {
			try {
				this.factoryEffectDispose()
			} catch {
				// cleanup shouldn't throw; ignore anything that does.
			}
			this.factoryEffectDispose = undefined
		}
		if (this.positionerTeardown) {
			try {
				this.positionerTeardown()
			} catch {
				// cleanup shouldn't throw; ignore anything that does.
			}
			this.positionerTeardown = undefined
		}
		this.active = false
		this.removeAttribute('active')
		this.#closed$.next({ reason, result })
		this.#closed$.complete()
	}

	/* ---------------- render ------------------------------------------- */

	protected render(): TemplateResult {
		if (!this.active) return html``
		// Liquid-glass material from the shared theme tokens (the single
		// definition <schmancy-surface> also reads), so overlay and surface are
		// the same glass. The fill itself is CSS driven by the `opacity`
		// attribute (see the `:host([opacity=...]) .surface` rules in static
		// styles) — a Tailwind bg-opacity utility can't express the glass tier's
		// color-mix, and an anchored tier renders with no backdrop behind it, so
		// this fill is the only separation the mounted content gets from whatever
		// the overlay opened over. backdrop-filter itself lives in that same
		// static-styles `.surface` rule, not as a Tailwind utility here — see
		// that rule's comment for why.
		const baseClasses =
			'surface fixed pointer-events-auto text-surface-on border border-[var(--schmancy-sys-glass-border)]'
		// Every surface sizes to its content within viewport clamps. Mounted
		// content declares any larger definite width or height on its own root.
		//
		// `sheet` is one layout token rendered two ways — bottom-anchored, or a
		// centered content-sized card — and which one is a question about the box
		// the sheet lays out in, which is not always the window.
		//
		// `media` (every unbounded overlay) leaves that question to CSS: the `md:`
		// prefixes mirror `isMobile$`'s 768px breakpoint and re-pin the surface to
		// a centered card — `md:inset-0 md:m-auto` (all four insets pinned + auto
		// margins is the standard fixed-position centering technique) with
		// `md:h-fit` so height resolves to shrink-to-fit. `md:pb-0` resets the safe-area padding the
		// bottom-anchored sheet needs to clear the home-indicator strip; the
		// centered card never touches that edge.
		//
		// `bottom`/`centered` are the same two renders with the prefixes already
		// resolved, because a bounded overlay's box is its boundary's rect and a
		// media query can only see the window — a phone frame drawn on a 1280px
		// screen would otherwise show the desktop card inside the drawn phone.
		//
		// `viewer` is the anchored string with the frame taken off, and nothing
		// else. Same `w-max`, same viewport caps, same placement — it differs only
		// by dropping `rounded-3xl shadow-overlay-anchored`, because a radius and a
		// shadow are a frame drawn around the content and this layout exists to
		// draw nothing around the content. It forces no size and no position of its
		// own: the picture is the size the picture is, and the anchor the gesture
		// gave it decides where it sits, exactly as for every other `show()`. Its
		// fill, blur and hairline go in static CSS rather than here — see the
		// `:host([layout='viewer']) .surface` rule for why a class cannot do it.
		const layoutClasses =
			this.layout === 'viewer'
				? // The anchored string with the frame taken off, and nothing else — so the
					// viewer is placed by the same Floating UI pass as every other anchored
					// surface and opens where the gesture put it, rather than carrying a
					// placement of its own.
					//
					// WITH NO ANCHOR IT MUST CENTRE ITSELF, because nothing else will. An
					// anchorless viewer — lightbox.push() from a timer, a stream, a state
					// change — resolves no anchor, so it lands on the modal tier, and that
					// branch only promotes the shell: it runs no positioner at all. A
					// `fixed` surface with `w-max` and no insets then resolves left/top to
					// auto and sits in the viewport's top-left corner. `inset-0` with auto
					// margins is the same centring the sheet's own centred render uses.
					//
					// overflow-visible is the one divergence, and it is not a placement
					// decision: the picture is DRAGGED out of this box on dismissal, and a
					// clip cut to its own outline would slide it behind an invisible edge.
					(this.resolvedAnchor ? '' : 'inset-0 m-auto h-fit ') +
					'w-max min-h-[2.75rem] max-w-[calc(100dvw-2rem)] max-h-[90svh] flex flex-col overflow-visible'
				: this.layout !== 'sheet'
					? 'w-max min-h-[2.75rem] max-w-[calc(100dvw-2rem)] max-h-[90svh] rounded-3xl shadow-overlay-anchored flex flex-col overflow-hidden'
					: this.sheetRender === 'bottom'
						? 'left-0 right-0 bottom-0 w-full max-h-[90svh] rounded-t-3xl pb-[env(safe-area-inset-bottom)] shadow-overlay flex flex-col overflow-hidden'
						: this.sheetRender === 'centered'
							? 'inset-0 m-auto w-max max-w-[calc(100dvw-2rem)] max-h-[90svh] h-fit rounded-3xl shadow-overlay flex flex-col overflow-hidden'
							: 'left-0 right-0 bottom-0 w-full max-h-[90svh] rounded-t-3xl pb-[env(safe-area-inset-bottom)] ' +
								'md:inset-0 md:m-auto md:w-max md:h-fit md:max-w-[calc(100dvw-2rem)] md:rounded-3xl md:pb-0 ' +
								'shadow-overlay flex flex-col overflow-hidden'
		return html`
			<div class="shell fixed inset-0 pointer-events-none" part="shell">
				${when(
					this.modal,
					() =>
						html`<div
							class="backdrop fixed inset-0 pointer-events-auto"
							part="backdrop"
							@click=${this.onBackdropClick}
						></div>`,
				)}
				<section
					class="${baseClasses} ${layoutClasses}"
					part="surface"
					data-focus-scope
					data-layout=${this.layout}
					data-tier=${this.tier}
					role=${this.modal || this.sticky || this.layout === 'viewer' ? 'dialog' : 'region'}
					aria-modal=${this.modal || this.layout === 'viewer' ? 'true' : 'false'}
					aria-label=${ifDefined(this.surfaceLabel)}
					tabindex="-1"
					@click=${this.onSurfaceClick}
				>
					${when(
						this.fileViewer,
						() => html`
							<div class="absolute right-2 top-1 z-20 flex items-center gap-1">
								<schmancy-icon-button
									part="maximize-control"
									size="xs"
									.icon=${this.maximized ? 'fullscreen_exit' : 'fullscreen'}
									aria-label=${componentText(this.maximized ? 'overlay.restoreViewer' : 'overlay.maximizeViewer')}
									aria-pressed=${this.maximized}
									@click=${() => { this.maximized = !this.maximized }}
								></schmancy-icon-button>
								<schmancy-icon-button
									size="xs"
									icon="close"
									part="close-control"
									aria-label=${componentText('overlay.closeViewer')}
									@click=${() => {
										if (this.dispatchEvent(new CustomEvent<OverlayBeforeCloseDetail>('beforeclose', { detail: { reason: 'structured' }, cancelable: true }))) void this.close('structured')
									}}
								></schmancy-icon-button>
							</div>
						`,
					)}
					${when(
						this.drag,
						// THE DRAG HANDLE, OVER the content rather than above it. As a flex
						// child of this column it charged every draggable surface in the
						// library 20px of content height for an affordance most of them are
						// never dragged by; out of flow it charges none.
						//
						// Which makes its FOOTPRINT the whole difficulty. A full-width strip
						// laid over the content would swallow every tap meant for whatever
						// the mount renders at its top edge — on a date panel that band is
						// the day, the editable HH:mm readout and the `Now` button, and all
						// three would go dead under a touch-none grab layer. So the rail
						// spans the width only to CENTRE the grabber and takes no pointer
						// events at all; the grabber takes them back for itself alone, as a
						// pill narrow enough that the dead zone is one predictable spot
						// instead of a band, with everything either side of it reachable.
						//
						// `.drag-handle` stays the class and stays focusable: the `@query`
						// above, `wireDragToReposition`, and both `focusableDescendants(…)`
						// filters find the handle by exactly that.
						() => html`
							<div class="absolute inset-x-0 top-0 z-10 flex justify-center pointer-events-none">
								<div
									class="drag-handle pointer-events-auto flex items-center justify-center h-5 w-16 cursor-grab touch-none select-none outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
									part="drag-handle"
									role="button"
									tabindex="0"
									aria-label=${this.dragLabel}
								>
									<span class="h-1.5 w-10 rounded-full bg-outline-variant" aria-hidden="true"></span>
								</div>
							</div>
						`,
					)}
					${when(
						this.sheetChrome && this.sheetRender !== 'centered',
						// THE SHEET'S OWN DISMISS CHROME, in flow above the content so it
						// covers none of it. The handle is the visible half of the swipe the
						// top band already arms, and the row is touch-none so a finger's pull
						// reaches that swipe rather than being claimed by the browser as a
						// pan and cancelled; the close is the path for a reader who cannot
						// swipe. At the window's own `md` width the media render turns
						// into the centered card, which has neither, so the row leaves with
						// it.
						() => html`
							<div
								data-sheet-chrome
								part="sheet-chrome"
								class=${this.classMap({
									'relative flex flex-none justify-center touch-none select-none': true,
									'h-5': this.contentClose,
									'h-11': !this.contentClose,
									'md:hidden': this.sheetRender === 'media',
								})}
							>
								<span class="mt-2 h-1.5 w-10 rounded-full bg-outline-variant" aria-hidden="true"></span>
								${when(!this.contentClose, () => html`
								<schmancy-icon-button
									class="absolute right-2 top-1"
									size="sm"
									icon="close"
									part="close-control"
									aria-label=${this.surfaceLabel
										? componentText('overlay.closeNamed', { label: this.surfaceLabel })
										: componentText('overlay.close')}
									@click=${() => {
										if (this.allowsDismiss('structured')) void this.close('structured')
									}}
								></schmancy-icon-button>
								`)}
							</div>
						`,
					)}
					<!-- flex-auto (flex-basis:auto), never flex-1 (flex-basis:0%). The surface
					     is capped by max-h alone, so its height is AUTO/indefinite, and
					     Safari resolves a 0% flex-basis against that indefinite height as
					     zero instead of running Chrome's intrinsic content-sizing pass:
					     the mount collapses, and the surface collapses with it. flex-auto
					     (flex-basis:auto) sizes from content, so it survives an indefinite
					     container; min-h-0 keeps it shrinkable so overflow-auto still
					     scrolls once content passes the cap. Swapping the surface to
					     display:-webkit-box masks this — legacy box ignores flex-basis
					     entirely — but drops min-height:0 scrolling and the md: centering
					     with it, so the basis is fixed here instead. -->
					<!-- A viewer never scrolls: its content is sized to the viewport it
					     was given, so a scrollport there could only mean the content
					     overflowed the screen — and dragging it is the dismissal
					     gesture, not a pan. -->
					<div
						id=${MOUNT_POINT_ID}
						class="flex-auto min-h-0 ${this.layout === 'viewer' ? 'overflow-visible' : 'overflow-auto'}"
					></div>
				</section>
			</div>
		`
	}

	private onBackdropClick = (): void => {
		if (isMobile$.value && currentStack().length && currentStack().at(-1)?.element !== this) return
		if (this.allowsDismiss('backdrop')) void this.close('backdrop')
	}

	/**
	 * The viewer's ground is the surface itself, so tapping it dismisses — the
	 * same gesture, and the same `CloseReason`, as tapping a backdrop.
	 *
	 * It is no longer the ONLY path — a viewer is content-sized and non-modal now,
	 * so a tap beside it lands outside the surface and `outsideClick$` answers it
	 * like any anchored panel. This listener covers what that one cannot: the
	 * ground INSIDE the surface's own box but outside its content, and a lightbox
	 * mounted standalone with no overlay above it to run an outside-click at all.
	 * It earns its place in both.
	 *
	 * There is no `.backdrop` to take the tap instead: a viewer is not modal, so
	 * `when(this.modal, …)` in render() emits none.
	 *
	 * `event.target === surface` is the whole test, and it is exact: anything
	 * mounted inside retargets to itself (or, across a shadow boundary, to its
	 * own host) and never to the surface, so this fires only for a tap that
	 * landed on ground no content is occupying. Content that fills the surface
	 * owns its own empty margins and dismisses from there itself.
	 */
	private onSurfaceClick = (event: MouseEvent): void => {
		if (this.layout !== 'viewer') return
		if (isMobile$.value && currentStack().length && currentStack().at(-1)?.element !== this) return
		if (event.target !== this.surface) return
		if (this.allowsDismiss('backdrop')) void this.close('backdrop')
	}

	/**
	 * Whether a dismiss the user started — Escape, a backdrop or outside click,
	 * the trigger toggle, a swipe, Back — may close this overlay. One already
	 * closing lets every dismiss through: there is nothing left to keep.
	 * `dismissable: false` refuses every one. Otherwise the cancelable
	 * `beforeclose` event asks whoever listens on this host, and on it alone;
	 * mounted content that refuses can confirm on its own and then close for
	 * real with its `close` event, which is its own intent and never asks.
	 */
	allowsDismiss(reason: CloseReason): boolean {
		return (
			this.closing ||
			(this.dismissable &&
				this.dispatchEvent(
					new CustomEvent<OverlayBeforeCloseDetail>('beforeclose', { detail: { reason }, cancelable: true }),
				))
		)
	}

	/* ---------------- anchor-origin bloom ------------------------------ */

	private setAnchorOriginVars(): void {
		const surface = this.surface
		if (!surface) return
		const rect = surface.getBoundingClientRect()
		const vars =
			this.layout === 'sheet'
				? { '--schmancy-overlay-origin-x': '50%', '--schmancy-overlay-origin-y': '100%' }
				: anchorOriginVars(this.anchorOriginAnchor, rect)
		for (const [k, v] of Object.entries(vars)) {
			surface.style.setProperty(k, v)
		}
	}

	/* ---------------- focus trap --------------------------------------- */

	private wireFocusTrap(): void {
		// `document.activeElement` stops at the outermost shadow host: a trigger
		// inside another overlay's content reads as that overlay's own host, which
		// is not focusable, so focus came back to <body> on release.
		const trigger = deepActiveElement()
		this.lastFocusedElement = trigger instanceof HTMLElement ? trigger : null

		// Inerting siblings is a modal-only concern: an anchored, non-modal
		// popover (schmancy-menu) deliberately leaves the rest of the page
		// interactive behind it, so only a modal surface (backdrop-covered)
		// removes the page from the accessibility tree while open.
		//
		// Through `claimInert`, never `child.inert = true` directly: modals stack
		// and do not unwind in order, so what this overlay releases at close has
		// to account for whatever opened above it and is still holding the same
		// sibling down. The count lives in overlay.stack; this list is only which
		// elements THIS overlay claimed, so its release is exact.
		// `modal` is a POSITIONING fact — it decides the tier, and a viewer is not
		// modal precisely so it keeps the ordinary anchored placement. Reaching the
		// page behind is a different question, and gating it on `modal` quietly tied
		// the two together: making the viewer non-modal took its focus containment
		// away with its full-bleed geometry, and since the page behind a photo is now
		// fully visible, Tab walked straight off the picture into it. A viewer is a
		// thing you are looking AT; what is behind it is not reachable while it is
		// open, whatever size it happens to be.
		if (this.modal || this.layout === 'viewer') {
			const parent = this.parentElement
			if (parent) {
				this.inertedSiblings = []
				for (const child of Array.from(parent.children)) {
					if (inertableSibling(child, this)) {
						claimInert(child)
						this.inertedSiblings.push(child)
					}
				}
			}
		}

		// Moving initial focus into the mounted surface is not modal-only —
		// every overlay tier (anchored popover included) must move focus off
		// the trigger or `<body>` on open, or a keyboard/AT user's first Tab
		// escapes straight to whatever sits behind the still-open surface.
		//
		// A control whose shadow root has not rendered yet has no box, and the
		// platform ignores `.focus()` on it without a word — so the pick waits one
		// task for the content's renders to commit, and keeps the first candidate
		// that actually holds focus.
		const holdsFocus = (surface: HTMLElement): boolean =>
			surface.matches(':focus-within') && deepActiveElement() !== surface
		timer(0)
			.pipe(
				map(() => this.surface),
				// Closing already hands focus back to the trigger.
				filter(surface => !this.closing && surface !== null),
				// The content already placed focus itself — a field's own `autofocus`,
				// a prompt focusing its input on first render — or the user did.
				filter(surface => !holdsFocus(surface)),
				takeUntil(this.disconnecting),
			)
			.subscribe(surface => {
				// The drag handle sits first in DOM order (it renders above the mount
				// point) but is chrome, not content — initial focus skips it so it
				// lands on the mounted content's own first control instead, exactly
				// as it would with `drag` off.
				// The sheet's close is chrome too, and lives inside an icon button's
				// own shadow root, so it is recognised by that host.
				const isChrome = (el: HTMLElement): boolean => {
					const root = el.getRootNode()
					return (
						el.classList.contains('drag-handle') ||
						(root instanceof ShadowRoot && root.host.closest('[data-sheet-chrome]') !== null)
					)
				}
				const candidates = [
					firstAutofocusTarget(surface),
					...focusableDescendants(surface).filter(el => !isChrome(el)),
				]
				for (const candidate of candidates) {
					candidate?.focus()
					if (holdsFocus(surface)) return
				}
				surface.focus()
			})
	}

	private releaseFocusTrap(): void {
		for (const el of this.inertedSiblings) {
			releaseInert(el)
		}
		this.inertedSiblings = []
		const surface = this.surface
		// Focus is the overlay's to hand back only while it is inside the surface
		// or dropped to `<body>`; a control the person moved to elsewhere keeps it.
		const surfaceOwnsFocus = (): boolean => {
			const active = deepActiveElement()
			return active === null || active === document.body || surface?.matches(':focus-within') === true
		}
		try {
			if (surfaceOwnsFocus()) {
				this.lastFocusedElement?.focus?.({ preventScroll: true })
				// The recorded element did not take focus: it was `<body>` (WebKit
				// never focuses a control a pointer pressed) or it has left the page.
				// The control the gesture pressed is found the way Chromium's own click
				// finds it — the pressed point's nearest flat-tree ancestor that takes
				// focus.
				for (
					let at: Node | null = this.triggerEl ?? null;
					at && surfaceOwnsFocus();
					at = at instanceof ShadowRoot ? at.host : (at instanceof Element && at.assignedSlot) || at.parentNode
				) {
					if (at instanceof HTMLElement) at.focus({ preventScroll: true })
				}
			}
		} catch {
			// trigger may be detached now; no-op.
		}
		this.lastFocusedElement = null
	}

	/**
	 * Tab from inside the mounted content must never escape back to the
	 * underlying page while the overlay is visually open and occluding it —
	 * this is the shared shell every `schmancy-menu`, `show()` dialog, and
	 * sheet is built on, so the trap lives here once rather than in each of
	 * them. Applies regardless of `modal` tier: an anchored, non-modal
	 * `schmancy-menu` popover is just as reachable-only-by-mouse without it.
	 */
	private wireTabTrap(): void {
		this.tabTrapHandler = (e: KeyboardEvent): void => {
			if (e.key !== 'Tab') return
			const focusable = focusableDescendants(this.surface)
			if (focusable.length === 0) {
				// No focusable descendant — the surface itself (tabindex="-1") is
				// the only thing holding focus, so Tab must be swallowed entirely
				// or it escapes past it to the page.
				e.preventDefault()
				return
			}
			// Authoritative, not boundary-only: macOS Safari's default (Full
			// Keyboard Access off) skips non-text controls during native Tab, so
			// focus never actually lands on `last` and the old boundary check
			// (active === first/last) never fires — Tab escapes straight to the
			// browser chrome. Compute the next focusable index ourselves on every
			// Tab, wrapping, and always move focus there — containment no longer
			// depends on native Tab reaching each control.
			const active = deepActiveElement()
			const activeIndex = active instanceof HTMLElement ? focusable.indexOf(active) : -1
			const step = e.shiftKey ? -1 : 1
			const start = activeIndex === -1 ? (e.shiftKey ? focusable.length : -1) : activeIndex
			e.preventDefault()
			// A stop can still refuse `.focus()` for a reason the list cannot read;
			// the press moves on to the stop after it rather than holding focus.
			for (let offset = 1; offset <= focusable.length; offset++) {
				const next = focusable[(start + step * offset + focusable.length) % focusable.length]
				if (next === active) return
				next.focus()
				if (deepActiveElement() === next) return
			}
		}
		this.addEventListener('keydown', this.tabTrapHandler)
	}

	private releaseTabTrap(): void {
		if (!this.tabTrapHandler) return
		this.removeEventListener('keydown', this.tabTrapHandler)
		this.tabTrapHandler = undefined
	}

	/* ---------------- close triggers ----------------------------------- */

	private wireCloseTriggers(signal?: AbortSignal): void {
		const until = this.disconnecting

		// Handle already-aborted signal synchronously — no stream needed.
		if (signal?.aborted) {
			queueMicrotask(() => void this.close('abort'))
			return
		}

		// Structured close — content dispatches CustomEvent('close', { detail }).
		const structured$ = fromEvent<CustomEvent>(this, 'close').pipe(
			filter(e => e instanceof CustomEvent),
			tap(e => e.stopPropagation()),
			map(e => ({ reason: 'structured' as CloseReason, result: e.detail })),
		)

		// Native <form method="dialog"> submission bubbles up as a regular
		// submit event with `submitter.value` (returnValue proxy for our
		// custom shell). Capture it and resolve with the string value.
		const nativeSubmit$ = fromEvent<SubmitEvent>(this, 'submit').pipe(
			filter(e => {
				const form = e.target as HTMLFormElement | null
				return !!form && form.method === 'dialog'
			}),
			tap(e => e.preventDefault()),
			map(e => {
				const submitter = (e as SubmitEvent & { submitter?: HTMLButtonElement | HTMLInputElement }).submitter
				return { reason: 'native-submit' as CloseReason, result: submitter?.value ?? '' }
			}),
		)

		// Manual Esc — all tiers. Modal has no native dismiss; anchored
		// tiers use `popover="manual"` so the browser doesn't auto-Esc them
		// either (the auto popover-stack would close ancestor overlays when
		// a nested overlay opens — see positionPopoverAPI's comment).
		//
		// Every stacked instance subscribes its own escape$ to the same
		// document keydown, so one Escape press reaches all of them — only
		// the topmost may act on it, mirroring outsideClick$'s stack-aware
		// guard below. Read the stack fresh per keydown, not once at
		// subscribe time, since it mutates as overlays open/close.
		// preventDefault() stays unconditional on the key match (before the
		// dismissable/top-of-stack filters) so the browser never does
		// anything native with Escape while any overlay is open, regardless
		// of which instance's listener happens to run first.
		const escape$ = fromEvent<KeyboardEvent>(document, 'keydown').pipe(
			filter(e => e.key === 'Escape'),
			tap(e => e.preventDefault()),
			filter(() => this.dismissable),
			filter(() => {
				const stack = currentStack()
				return stack.length > 0 && stack[stack.length - 1].element === this
			}),
			map(() => ({ reason: 'escape' as CloseReason })),
		)

		// Manual outside-click — anchored tiers that no caller has declared
		// sticky. Modal layouts have a backdrop that catches outside clicks
		// via `onBackdropClick`; the anchored tiers (no backdrop) need a
		// document-level pointerdown listener that ignores clicks whose
		// composedPath includes the surface or anchor. Use composedPath
		// membership (not `Node.contains`) so clicks on elements inside slotted
		// / shadow-DOM descendants of the surface are correctly classified as
		// "inside".
		//
		// A stray pointerdown dismissing the surface is the menu contract: the
		// panel belongs to the trigger, and attention moving away ends it. A
		// sticky surface carries the answer to what the user just did, so
		// attention moving away is not consent to lose it — it closes on Escape,
		// on a control it renders, or on a backdrop where its layout has one.
		// A viewer is included whatever its tier. It draws no backdrop (it is not
		// modal), so `onBackdropClick` never fires for it, and an anchorless one sits
		// on the modal tier — which without this is a picture with Escape as its only
		// exit. Clicking away from a photo has to close it.
		const outsideClick$ =
			(this.tier !== 'modal' || this.layout === 'viewer') && !this.sticky
				? fromEvent<PointerEvent>(document, 'pointerdown', { capture: true }).pipe(
						filter(e => {
							if (!this.dismissable) return false
							if (isMobile$.value && (!e.isPrimary || e.button !== 0 || currentStack().at(-1)?.element !== this)) {
								return false
							}
							const path = e.composedPath()
							if (this.surface && path.includes(this.surface)) return false
							if (path.some(node => node instanceof Element && node.hasAttribute('data-overlay-exempt'))) {
								// The exemption shields a panel from a layer it does not belong to. One
								// whose content root is marked, or whose host is mounted inside the layer,
								// is that layer's own: a tap elsewhere in it is an ordinary outside tap.
								const ownLayer =
									!!this.renderRoot.querySelector(`#${MOUNT_POINT_ID} > [data-overlay-exempt]`) || inExemptLayer(this)
								// Inspector controls preserve the inspected panel; its own trigger still toggles it.
								const trigger = anchorTrigger(this.resolvedAnchor)
								if (!ownLayer && (!trigger || !path.includes(trigger))) return false
							}
							// Suppress dismiss when the click lands inside any overlay that sits
							// ABOVE this one in the stack (nested overlays sibling-appended to
							// body). Read the stack fresh per pointerdown — it mutates as overlays
							// open and close.
							const stack = currentStack()
							const myIndex = stack.findIndex(entry => entry.element === this)
							if (myIndex !== -1) {
								for (let i = myIndex + 1; i < stack.length; i++) {
									if (path.includes(stack[i].element)) return false
									const aboveSurface = stack[i].element.shadowRoot?.querySelector('.surface')
									if (aboveSurface && path.includes(aboveSurface)) return false
								}
							}
							return true
						}),
						// A pointerdown on this panel's own trigger dismisses like any other,
						// and additionally swallows the click it leads to, so the control's
						// handler cannot re-open what this just closed. Closing here rather
						// than leaving the re-open for `show()` to decline is what keeps the
						// toggle independent of the consumer: a trigger that guards its own
						// open state never calls `show()` a second time, and a panel waiting
						// on that call would have no dismiss path left at all.
						//
						// `anchorTrigger`, never `resolvedAnchor.el` — `el` is populated only
						// for element anchors, so on the point anchor every gesture-opened
						// overlay carries, reading `el` left this guard dead.
						map(e => {
							const trigger = anchorTrigger(this.resolvedAnchor)
							const onTrigger = !!trigger && e.composedPath().includes(trigger)
							// Arm before dismissal can remove the host or a beforeclose listener can refuse.
							if (isMobile$.value) suppressNextClick(e)
							else if (onTrigger) suppressNextClick()
							return onTrigger
						}),
						map(onTrigger => ({ reason: (onTrigger ? 'toggle' : 'backdrop') as CloseReason })),
					)
				: null

		// Swipe-to-dismiss for the sheet layout's bottom-anchored render only —
		// where the same layout token centers as a card, a top-band drag would read
		// as an accidental dismiss rather than a deliberate pull-down. Which render
		// is showing is the window's answer (`isMobile$`) only while the overlay is
		// unbounded; a bounded one already resolved it against its boundary, and
		// arming off the window there would leave a live swipe on a 1280px screen
		// where the surface reads as a card. With no handle passed, the drag starts
		// from the surface's own top band (`DRAG_START_TOP_PX` in overlay.gestures),
		// whose interactive-descendant filter keeps a top-bar close button clickable
		// inside that band. Escape + backdrop click cover the other dismiss paths
		// via the modal-tier listeners above.
		const swipe$ =
			this.layout === 'sheet' &&
			this.dismissable &&
			(this.sheetRender === 'media' ? isMobile$.value : this.sheetRender === 'bottom')
				? swipeToDismiss$({
						surface: this.surface,
						until$: merge(until, this.#closed$),
						allows: () => this.allowsDismiss('swipe'),
					}).pipe(map(() => ({ reason: 'swipe' as CloseReason })))
				: null

		// AbortSignal — standard cancellation input.
		const abort$ = signal
			? fromEvent(signal, 'abort').pipe(
					take(1),
					map(() => ({ reason: 'abort' as CloseReason })),
				)
			: null

		// A dismiss the user started asks first. A refused one leaves every leg
		// listening, so only the close that goes ahead ends this subscription. The
		// swipe asks from inside its gesture, which owns the snap-back a refusal needs.
		merge(
			structured$,
			nativeSubmit$,
			merge(escape$, outsideClick$ ?? EMPTY).pipe(filter(({ reason }) => this.allowsDismiss(reason))),
			swipe$ ?? EMPTY,
			abort$ ?? EMPTY,
		)
			.pipe(take(1), takeUntil(until))
			.subscribe(({ reason, result }: { reason: CloseReason; result?: unknown }) => void this.close(reason, result))
	}

	/* ---------------- bounded sheet variant ---------------------------- */

	/** The content marks its real Close; the sheet only supplies a fallback. */
	private wireContentClose(mount: HTMLElement): void {
		if (!this.sheetChrome) return
		const contentChanges$ = new Observable<void>(subscriber => {
			let observed = new Set<Element | ShadowRoot>()
			const observe = () => {
				const roots: (Element | ShadowRoot)[] = [mount]
				for (const root of roots)
					for (const element of root.querySelectorAll('*'))
						if (element.shadowRoot) roots.push(element.shadowRoot)
				if (roots.length !== observed.size || roots.some(root => !observed.has(root))) {
					observer.disconnect()
					observer.observe(this.surface, { attributes: true, attributeFilter: ['style'] })
					for (const root of roots)
						observer.observe(root, { subtree: true, childList: true, attributes: true })
					observed = new Set(roots)
				}
				subscriber.next()
			}
			const observer = new MutationObserver(observe)
			observe()
			return () => observer.disconnect()
		})
		merge(contentChanges$, fromResizeObserver(mount), fromEvent(window, 'resize'))
			.pipe(
				map(() => focusableDescendants(mount).some(control => {
					if (control.matches(':disabled') || !control.checkVisibility({ checkVisibilityCSS: true })) return false
					let at: Element | null = control
					let marked = false
					while (at && at !== mount) {
						if (at.hasAttribute('disabled') || at.getAttribute('aria-disabled') === 'true' || getComputedStyle(at).opacity === '0') return false
						if (at.hasAttribute('data-overlay-close')) marked = true
						const root = at.getRootNode()
						at = at.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
					}
					if (marked) {
						const rect = control.getBoundingClientRect()
						const surface = this.surface.getBoundingClientRect()
						const box = this.bounded ? this.getBoundingClientRect() : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
						// Entrance and swipe transforms move the whole surface temporarily;
						// its cleared will-change signals when viewport containment is final.
						return rect.width > 0 && rect.height > 0 && getComputedStyle(control).visibility === 'visible' &&
							(this.surface.style.willChange === 'transform' || (
								rect.left >= Math.max(surface.left, box.left) - 1 && rect.right <= Math.min(surface.right, box.right) + 1 &&
								rect.top >= Math.max(surface.top, box.top) - 1 && rect.bottom <= Math.min(surface.bottom, box.bottom) + 1))
					}
					return false
				})),
				distinctUntilChanged(),
				tap(owned => { this.contentClose = owned }),
				takeUntil(merge(this.disconnecting, this.#closed$)),
			)
			.subscribe()
	}

	/**
	 * A boundary is a real box on the page — a device-frame mock-up whose size
	 * tracks its own layout — so the sheet's variant is re-resolved whenever that
	 * box changes, exactly as the `md:` media query re-resolves for an unbounded
	 * sheet when the window is resized. No-op unbounded, where CSS already owns
	 * the whole question.
	 */
	private wireBoundedSheetRender(): void {
		if (!this.bounded) return
		fromResizeObserver(this)
			.pipe(
				map(entries => entries[0]?.contentRect.width),
				filter((width): width is number => width !== undefined),
				map(width => sheetRenderFor(width)),
				distinctUntilChanged(),
				tap(render => {
					this.sheetRender = render
				}),
				takeUntil(merge(this.disconnecting, this.#closed$)),
			)
			.subscribe()
	}

	/* ---------------- iOS keyboard avoidance (sheet, mobile) ----------- */

	/**
	 * iOS shrinks only the visual viewport for the soft keyboard — `dvh`,
	 * `window.resize`, and a fixed/top-layer surface's own layout are all
	 * blind to it — so a bottom sheet pinned at `bottom:0` stays anchored to
	 * the (unshrunk) layout viewport's bottom edge, now hidden behind the
	 * keyboard. Mirrors the visualViewport idiom in
	 * `@schmancy/directives/layout`'s `getUpdateStream` (resize+scroll,
	 * passive, iOS focusout recalc). Scoped to the sheet layout's mobile,
	 * unbounded render — a `bounded` overlay is sized against its boundary box,
	 * not the real viewport, so `window.innerHeight`/`visualViewport` are the
	 * wrong reference for it.
	 *
	 * Offsets via `bottom`, not `transform` — a property the swipe gesture
	 * and the entrance/exit WAAPI animations never touch, so this can never
	 * fight either of them for the same CSS property. `max-height` is capped
	 * alongside it so a tall sheet's top edge can't cross above y=0; the
	 * mount's own flex/min-h-0 shrink absorbs the rest via internal scroll.
	 * `isMobile$` is folded into the pipeline itself (not just the initial
	 * gate) so crossing the `md` breakpoint mid-session clears a stale
	 * offset immediately instead of skewing the now-centered card.
	 *
	 * The same recalculation also re-homes whichever field is focused: shrinking
	 * `maxHeight` can strand the field the keyboard was opened for above the new
	 * bottom edge or behind the keyboard itself, and this is the one point in the
	 * pipeline where the real keyboard has actually finished moving — a `focusin`
	 * listener firing this same nudge would read `overlap` before the keyboard
	 * animates in and land on 0/stale, racing this recalculation the way
	 * `item-card.view.ts`'s own consumer-level fix once did. `focusableDescendants`
	 * doubles as the composed-tree containment check: real descendants of
	 * `this.surface` (`show()` mounts content into `#overlay-mount` via
	 * `litRender`/`appendChild`, never through a `<slot>`) that also happen to be
	 * the current `deepActiveElement()` are exactly the ones this overlay owns.
	 */
	private wireKeyboardAvoidance(): void {
		const vv = window.visualViewport
		if (!vv || this.layout !== 'sheet' || this.bounded || !isMobile$.value) return

		const viewportEvents$ = merge(
			fromEvent(vv, 'resize', { passive: true }),
			fromEvent(vv, 'scroll', { passive: true }),
		)
		// iOS Safari: visualViewport doesn't always settle to its final height
		// until after the keyboard-dismissing blur fires.
		const keyboardClose$ = fromEvent(document, 'focusout', { passive: true }).pipe(
			switchMap(() => timer(KEYBOARD_CLOSE_RECALC_MS)),
		)

		merge(viewportEvents$, keyboardClose$, isMobile$)
			.pipe(
				debounceTime(VIEWPORT_DEBOUNCE_MS),
				map(() => (isMobile$.value ? Math.max(0, window.innerHeight - (vv.height + vv.offsetTop)) : 0)),
				distinctUntilChanged(),
				tap(overlap => {
					// The lift glides up from where the sheet sat, rather than landing
					// in one frame after the keyboard has already finished moving.
					glideY(this.surface, overlap - (parseFloat(this.surface.style.bottom) || 0), 0)
					this.surface.style.bottom = overlap > 0 ? `${overlap}px` : ''
					this.surface.style.maxHeight = overlap > 0 ? `${Math.max(0, window.innerHeight - overlap)}px` : ''
					if (overlap <= 0) return
					const focused = deepActiveElement()
					if (focused instanceof HTMLElement && focusableDescendants(this.surface).includes(focused)) {
						focused.scrollIntoView({ block: 'nearest', behavior: reducedMotion$.value ? 'instant' : 'smooth' })
					}
				}),
				takeUntil(merge(this.disconnecting, this.#closed$)),
			)
			.subscribe()
	}

	/* ---------------- drag-to-reposition (ShowOptions.drag) ------------- */

	/**
	 * No-op unless `this.drag` resolved `true` (see the resolution in
	 * `open()`) and the `.drag-handle` rendered. Wires the handle's whole
	 * open lifetime — reposition can happen any number of times, unlike the
	 * one-shot swipe dismiss.
	 */
	private wireDragToReposition(): void {
		if (!this.drag || !this.dragHandleEl) return
		const until = merge(this.disconnecting, this.#closed$)

		dragToReposition$({
			surface: this.surface,
			handle: this.dragHandleEl,
			// Drag wins over live anchor tracking: the FUI tiers' own
			// `autoUpdate` loop would otherwise fight the user's placement on
			// the next scroll/resize tick (it rewrites `transform` to `none`
			// every time it runs — see `positionFloatingUI`). `css-anchor`'s
			// native tracking and the sheet's CSS positioning have nothing to
			// tear down here; the translate composes on top of either.
			onDragStart: () => {
				this.trackingSub?.unsubscribe()
				this.trackingSub = undefined
			},
			until$: until,
		})
			.pipe(takeUntil(until))
			.subscribe()

		// A dragged surface has no live tracker left to re-answer a viewport
		// change (see above), and the viewport itself just moved under a fixed
		// translate even on the tiers that never had one — so reset to zero
		// rather than risk stranding the surface off-screen. The underlying
		// anchor/sheet positioning is untouched by the reset and repaints
		// correctly on its own once the translate is gone. Only a window that
		// really changed size counts: a soft keyboard fires `resize` with the
		// layout viewport untouched, and moved nothing the user placed.
		fromEvent(window, 'resize')
			.pipe(
				debounceTime(VIEWPORT_DEBOUNCE_MS),
				startWith(undefined),
				map(() => `${window.innerWidth}x${window.innerHeight}`),
				distinctUntilChanged(),
				skip(1),
				tap(() => {
					if (this.surface) this.surface.style.transform = ''
				}),
				takeUntil(until),
			)
			.subscribe()
	}

	/* ---------------- animations --------------------------------------- */

	/** The first positioning pass must settle before measuring the image's final box. */
	private async flipEntrance(direction: 'in' | 'out'): Promise<Keyframe[] | undefined> {
		if (direction !== 'in' || this.layout !== 'viewer') return undefined
		const first = this.triggerRect
		if (!first || !this.triggerEl?.isConnected || !this.surface) return undefined
		const surface = this.surface
		const viewer = surface.querySelector('schmancy-lightbox')
		if (!viewer) return undefined
		await viewer.updateComplete
		const media = viewer.shadowRoot?.querySelector<HTMLImageElement>('.media')
		if (!media || this.closing) return undefined
		const visibility = media.style.visibility
		// Hiding only the picture keeps the loading state and initial focus available.
		if (!reducedMotion$.value) media.style.visibility = 'hidden'
		const until = merge(this.#closed$, this.disconnecting)
		try {
			if (!media.complete) {
				await firstValueFrom(
					merge(fromEvent(media, 'load'), fromEvent(media, 'error')).pipe(take(1), takeUntil(until)),
					{ defaultValue: undefined },
				)
			}
			if (this.closing || !media.isConnected || !media.naturalWidth || !media.naturalHeight) return undefined
			await viewer.updateComplete
			await firstValueFrom(
				this.positioned$.pipe(
					filter(box => box.width === surface.offsetWidth && box.height === surface.offsetHeight),
					take(1),
					takeUntil(until),
				),
				{ defaultValue: undefined },
			)
			if (this.closing || !surface.isConnected) return undefined
			this.setAnchorOriginVars()
			if (reducedMotion$.value) return undefined
			const last = media.getBoundingClientRect()
			if (last.width <= 0 || last.height <= 0) return undefined
			const surfaceRect = surface.getBoundingClientRect()
			const origin = `${last.left - surfaceRect.left}px ${last.top - surfaceRect.top}px`
			return [
				{
					transform: `translate(${first.left - last.left}px, ${first.top - last.top}px) scale(${first.width / last.width}, ${first.height / last.height})`,
					transformOrigin: origin,
					opacity: 1,
				},
				{ transform: 'translate(0px, 0px) scale(1)', transformOrigin: origin, opacity: 1 },
			]
		} finally {
			if (!this.closing && surface.isConnected) media.style.visibility = visibility
		}
	}

	private async playAnimations(direction: 'in' | 'out'): Promise<void> {
		const surface = this.surface
		if (!surface) return
		// Floating UI measures asynchronously. Starting the bloom first changes
		// both its coordinates and its pivot while the entrance is already visible.
		if (direction === 'in' && (this.tier === 'popover-fui' || this.tier === 'fui-only')) {
			await firstValueFrom(
				this.positioned$.pipe(take(1), takeUntil(merge(this.#closed$, this.disconnecting))),
				{ defaultValue: undefined },
			)
		}
		const entrance = await this.flipEntrance(direction)
		if (direction === 'in' && (this.closing || !surface.isConnected)) return
		const spec = surfaceAnimation(this.layout, direction)
		if (entrance) spec.keyframes = entrance
		const mount = surface.querySelector(`#${MOUNT_POINT_ID}`)
		const viewer = this.layout === 'viewer' ? mount?.querySelector('schmancy-lightbox') : undefined
		// Fading a filtered descendant's ancestor cuts its backdrop off from the page.
		const fades = spec.keyframes.some(frame => frame.opacity !== undefined && String(frame.opacity) !== '1')
		const opacityAnim =
			viewer && viewer.parentElement === mount && fades
				? viewer.animateOverlayOpacity(direction, spec.options)
				: undefined
		const surfaceAnim = surface.animate(
			viewer && viewer.parentElement === mount && (!fades || opacityAnim)
				? spec.keyframes.map(frame => {
						// Even an opacity 1→1 animation creates an ancestor backdrop root.
						const geometry = { ...frame }
						delete geometry.opacity
						return geometry
					})
				: spec.keyframes,
			spec.options,
		)
		const tasks: Promise<unknown>[] = [surfaceAnim.finished.catch(() => undefined)]
		if (opacityAnim) tasks.push(opacityAnim.finished.catch(() => undefined))
		const backdrop = this.backdrop
		if (this.modal && backdrop) {
			tasks.push(
				backdrop
					.animate(direction === 'in' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], {
						duration: spec.options.duration,
						easing: direction === 'in' ? 'ease-out' : 'ease-in',
						fill: 'forwards',
					})
					.finished.catch(() => undefined),
			)
		}
		await Promise.all(tasks)
		// `fill: 'forwards'` (surfaceAnimation's own spec) keeps this Animation's
		// effect applied to `transform` indefinitely once finished — it outranks
		// the cascade, so a later direct `surface.style.transform` write (the
		// swipe gesture, `ShowOptions.drag`) is silently invisible without this:
		// commitStyles() bakes the held value into the inline style, then
		// cancel() removes the still-live effect, so the next write actually
		// renders. Only the entrance animation needs this — by the time 'out'
		// finishes, the overlay is closing and nothing writes transform again.
		if (direction === 'in') {
			for (const animation of opacityAnim ? [surfaceAnim, opacityAnim] : [surfaceAnim]) {
				try {
					// A close or gesture may have superseded the entrance while it awaited.
					if (!this.closing && animation.playState !== 'idle') animation.commitStyles()
					animation.cancel()
				} catch {
					// An entrance may finish after its content has been detached.
					animation.cancel()
				}
			}
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-overlay': SchmancyOverlay
	}
}

/* ======================================================================= *
 *                         content mounting helpers                         *
 * ======================================================================= */

async function mountContent(
	content: Content,
	host: HTMLElement,
	props?: Record<string, unknown>,
): Promise<HTMLElement> {
	// TemplateResult — render via lit's `render`.
	if (isTemplateResult(content)) {
		litRender(content, host)
		return host
	}

	// Already-instantiated element — append directly.
	if (content instanceof HTMLElement) {
		if (props) Object.assign(content, props)
		host.appendChild(content)
		return content
	}

	// LazyComponent — await the module, recurse with the default export.
	// Must precede the TemplateFactory check: a lazy loader is an arrow
	// function with no `prototype`, so isTemplateFactory would match it
	// first and call it as a factory.
	if (isLazy(content)) {
		const mod = await content()
		return mountContent(mod.default, host, props)
	}

	// TemplateFactory — call at mount time so closed-over variables are read lazily.
	if (isTemplateFactory(content)) {
		return mountContent(content(), host, props)
	}

	// Class constructor.
	if (typeof content === 'function') {
		const Ctor = content as unknown as { new (): HTMLElement }
		const el = new Ctor()
		if (props) Object.assign(el, props)
		host.appendChild(el)
		return el
	}

	// Tag name.
	if (typeof content === 'string') {
		const el = document.createElement(content)
		if (props) Object.assign(el, props)
		host.appendChild(el)
		return el
	}

	throw new Error('schmancy-overlay: unsupported content type')
}

function isTemplateFactory(x: unknown): x is import('./overlay.types').TemplateFactory {
	return typeof x === 'function' && !(x as { prototype?: unknown }).prototype
}

function isTemplateResult(x: unknown): x is TemplateResult {
	return typeof x === 'object' && x !== null && '_$litType$' in x
}

function isLazy(x: unknown): x is LazyComponent {
	return typeof x === 'function' && 'preload' in (x as object)
}
